import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Modal, ModalHead } from '@dixa/lib-shared-habla';
import pick from 'lodash/pick';
import memoize from 'memoize-one';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { useSetState } from 'react-use';
import { initialize, reset, SubmissionError } from 'redux-form';
import { v4 as uuid } from 'uuid';
import { analytics } from 'app/utils/segment';
import { AutomationNodeTypes, AutomationTrigger, QueuesApi, } from 'hocs';
import { useNotifications } from 'hooks/useNotifications';
import { FORM_NAME, validateEditFormStep1, validateEditFormStep2, } from '../utils/editAutomationForm';
import { EditAutomationFormStep1 } from './editAutomationFormStep1';
import { EditAutomationFormStep2 } from './editAutomationFormStep2';
import styles from './editAutomationModal.module.scss';
import { MatchingOperator, MatchingStrategy, MatchingSystemType, VariableScope, } from './nodeTypes/types';
const ALL_FORM_FIELDS = ['name', 'description', 'trigger', 'channels'];
const UPDATE_FORM_FIELDS = ['name', 'description'];
const getInitialValues = memoize((automationItems, automationId) => {
    if (automationId !== null) {
        const automation = automationItems.find((automation) => automation.id === automationId);
        if (automation) {
            return pick(automation, ALL_FORM_FIELDS);
        }
    }
    return {
        name: '',
        description: '',
        channels: [],
        trigger: AutomationTrigger.NONE,
    };
});
const EditAutomationModalImpl = ({ automations, automationId, defaultQueueId, gotoFlow, initializeForm, isOpen, onClose, refreshPage, resetForm, }) => {
    const [state, setState] = useSetState({
        formStep: 1,
        initialValues: getInitialValues(automations.items, automationId),
        defaultQueue: null,
    });
    const notify = useNotifications();
    const isCreating = automationId === null;
    useEffect(() => {
        if (isCreating) {
            QueuesApi.getQueue(defaultQueueId).then((res) => {
                if (res.data.id === defaultQueueId) {
                    setState({
                        defaultQueue: res.data,
                    });
                }
            });
        }
    }, []);
    useEffect(() => {
        setState({
            initialValues: getInitialValues(automations.items, automationId),
        });
    }, [automationId, automations.items]);
    useEffect(() => {
        if (isOpen) {
            analytics.automationsFormOpened({
                mode: isCreating ? 'create' : 'update',
            });
        }
    }, [isCreating, isOpen]);
    useEffect(() => {
        initializeForm(state.initialValues);
        resetForm();
    }, [initializeForm, resetForm, state.initialValues]);
    const handleFormStep1 = (values) => {
        analytics.automationsFormSubmitted({
            mode: isCreating ? 'create' : 'update',
            step: state.formStep.toString(),
        });
        if (isCreating) {
            setState({ formStep: 2 });
        }
        else {
            handleFormSubmit(values);
        }
    };
    const handleResetForm = () => {
        setState({ formStep: 1 });
        resetForm();
    };
    const getVerb = () => {
        return isCreating ? 'create' : 'update';
    };
    const getVerbPastTense = () => {
        return isCreating ? 'created' : 'updated';
    };
    const getTitle = () => {
        if (isCreating) {
            const { formStep } = state;
            if (formStep === 1) {
                return 'Add automation';
            }
            if (formStep === 2) {
                return 'Select a channel and a trigger';
            }
        }
        return 'Update automation';
    };
    const getCaption = () => {
        const { formStep } = state;
        if (formStep === 2) {
            return 'Select a channel and an action that will trigger this automation.';
        }
        return `Let's get started by giving the new automation a name and a description for better reference.`;
    };
    const createOrUpdate = (shouldCreate, payload) => {
        const { create, update } = automations;
        if (shouldCreate) {
            return create(payload);
        }
        return update(automationId, payload);
    };
    const handleFormSubmit = (values) => {
        if (!validateEditFormStep1(values) || !validateEditFormStep2(values)) {
            return undefined;
        }
        analytics.automationsFormSubmitted({
            mode: isCreating ? 'create' : 'update',
            step: state.formStep.toString(),
        });
        const actionVerb = getVerb();
        const actionVerbPastTense = getVerbPastTense();
        let payload;
        if (isCreating) {
            const { defaultQueue } = state;
            if (defaultQueue === null) {
                const notificationOptions = {
                    title: 'An error occurred',
                    text: `An error occurred when trying to get organization default queue`,
                    icon: 'exclamation',
                    className: 'danger',
                };
                notify(notificationOptions);
                return undefined;
            }
            const stopNodeID = uuid();
            payload = pick(values, ALL_FORM_FIELDS);
            payload.active = false;
            payload.actions = [
                {
                    id: 'root',
                    pageX: 0,
                    pageY: 0,
                    nodeType: AutomationNodeTypes.INIT,
                    parentId: null,
                    childIds: [stopNodeID],
                    connectionLabel: '',
                    payload: {
                        matchNodeId: stopNodeID,
                        matchingStrategy: MatchingStrategy.ALL,
                        rules: [
                            {
                                variable: {
                                    scope: VariableScope.SYSTEM,
                                    variableType: MatchingSystemType.QUEUE,
                                },
                                operator: MatchingOperator.INCLUDES_ONE,
                                match: {
                                    scope: VariableScope.INPUT,
                                    values: [
                                        {
                                            value: defaultQueue.id,
                                            meta: {
                                                label: defaultQueue.name,
                                            },
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                    isRemovable: false,
                    isEditable: true,
                },
                {
                    id: stopNodeID,
                    pageX: 0,
                    pageY: 150,
                    nodeType: AutomationNodeTypes.STOP,
                    parentId: 'root',
                    childIds: [],
                    connectionLabel: 'arrow',
                    payload: {},
                    isRemovable: true,
                    isEditable: false,
                },
            ];
        }
        else {
            // Channels & Trigger can't be updated
            payload = pick(values, UPDATE_FORM_FIELDS);
        }
        return createOrUpdate(isCreating, payload)
            .then((res) => {
            const notificationOptions = {
                title: `Automation ${actionVerbPastTense}`,
                text: `"${values.name}" automation has been ${actionVerbPastTense}`,
                icon: 'done',
                className: 'success',
                time: 5000,
            };
            notify(notificationOptions);
            onClose();
            handleResetForm();
            if (isCreating) {
                gotoFlow(`${res.data.id}#init`);
            }
            else {
                refreshPage();
            }
        })
            .catch(() => {
            const notificationOptions = {
                title: 'An error occurred',
                text: `An error occurred when trying to ${actionVerb} the "${values.name}" automation`,
                icon: 'exclamation',
                className: 'danger',
            };
            notify(notificationOptions);
            throw new SubmissionError({
                name: `Automation could not be ${actionVerbPastTense}`,
            });
        });
    };
    const handleCancel = () => {
        analytics.automationsFormCancelled({
            mode: isCreating ? 'create' : 'update',
            step: state.formStep.toString(),
        });
        onClose();
        handleResetForm();
    };
    const { formStep, initialValues } = state;
    return (_jsx(Modal, Object.assign({ isOpen: isOpen, onClose: handleCancel, className: styles.root }, { children: _jsxs(Box, Object.assign({ width: "600px" }, { children: [_jsx(ModalHead, Object.assign({ title: getTitle(), onClose: handleCancel }, { children: isCreating && _jsx("span", { children: getCaption() }) })), formStep === 1 && (_jsx(EditAutomationFormStep1, { initialValues: initialValues, handleCancel: handleCancel, nextStep: handleFormStep1, confirmName: isCreating ? 'Next' : 'Update' })), formStep === 2 && (_jsx(EditAutomationFormStep2, { initialValues: initialValues, handleCancel: handleCancel, handleFormSubmit: handleFormSubmit, isCreating: isCreating }))] })) })));
};
const mapStateToProps = (state) => {
    return {
        defaultQueueId: state.settings.organization.organization.default_queue,
    };
};
export const EditAutomationModal = connect(mapStateToProps, (dispatch) => {
    return {
        resetForm: () => dispatch(reset(FORM_NAME)),
        initializeForm: (payload) => dispatch(initialize(FORM_NAME, payload)),
    };
})(EditAutomationModalImpl);
